import { useRef, useState } from 'react'
import classNames from 'classnames'
import { observer } from 'mobx-react'
import './AddressForm.scss'
import PopupHeader from 'components/PopupHeader/PopupHeader'
import TextButton, { VARIANTS } from 'mixtiles-web-common/ui/TextButton'
import IconButton from 'mixtiles-web-common/ui/IconButton'
import { XIcon } from 'icons'
import { analytics, EVENT_NAMES } from 'services/Analytics/Analytics'
import * as EmailValidator from 'email-validator'
import * as S from './AddressForm.styles'
import {
  COUNTRIES,
  getDefaultCountryId,
  ADDRESS_INPUT_FIELDS,
} from 'config/countries-data'
import { useAddressStore } from 'stores/RootStoreProvider'
import {
  validateAddress,
  validateFullName,
  validateAddressLatinCharacters,
  shouldCapturePhone,
  getNameKanaError,
  NAME_KANA_ERROR_MESSAGE_KEYS,
} from 'stores/addressStore/address.utils'
import UserManager, { CAPTURE_EMAIL_SOURCE } from 'services/UserManager'
import { canVerifyCountryStates, verifyState } from 'utils/AddressUtils'
import { logger } from 'services/logger'
import StripeAddressForm from 'components/StripeAddressForm/StripeAddressForm'
import ErrorMessage from 'components/ErrorMessage/ErrorMessage'
import { useAvailableFrames } from '../FrameProvider/AvailableStockProvider'
import { gdprManager } from 'services/GdprMananger'
import { isMarketingConsentCountry } from 'utils/GDPR.utils'
import useOnMount from '../../hooks/useOnMount'
import { useTiles } from '../../hooks/useTiles'
import type { Address } from '../Pricing/types/checkout.types'
import { useKeys } from '../../services/KeysProvider'
import { getEmailConsentBraze } from 'api/userCapturedData.api'
import { toBrazeExternalId } from '../../services/Analytics/platforms/braze.utils'
import AddressValidationErrorDialog from 'components/AddressValidationErrorDialog/AddressValidationErrorDialog'
import { useLanguage } from '../../services/LangProvider'
import { LANGUAGES } from '../../locale/Translation.consts'
import { useTranslation } from 'react-i18next'

interface AddressFormProps {
  initialAddress?: Record<string, string>
  isUpdate?: boolean
  showPhoneError?: boolean
  onSubmitShippingAddress?: (address: Record<string, string>) => void
  onUpdate?: (address: Record<string, string>) => Promise<void>
  onClose: (newAddressAdded: boolean) => void
}

function initStateFromAddress(address: Record<string, string>) {
  try {
    const state: Record<string, string> = {}
    ADDRESS_INPUT_FIELDS.forEach((fieldName) => {
      state[fieldName] = address[fieldName]
    })

    // Get Saved full name from email capture, if any
    if (!state.fullName) {
      state.fullName = UserManager.getUserFullName()
    }

    state.countryId = address.countryId || getDefaultCountryId()
    if (!state.countryId) {
      logger.warning('No countryId in address object!')
    }
    return state
  } catch (e) {
    logger.error('Error initializing address form', e)
    return {}
  }
}

function AddressForm({
  initialAddress,
  isUpdate,
  showPhoneError,
  onSubmitShippingAddress,
  onUpdate,
  onClose,
}: AddressFormProps) {
  const addressStore = useAddressStore()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const language = useLanguage()
  const [email, setEmail] = useState<{
    value?: string
    valid?: boolean
    submitted?: boolean
  }>(() => {
    const userEmail = UserManager.getUserEmail()
    return userEmail ? { value: userEmail, valid: true, submitted: false } : {}
  })
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [formErrorMessage, setFormErrorMessage] = useState<string | null>('')
  const [validationError, setValidationError] = useState<string | null>('')
  const [isAddressComplete, setIsAddressComplete] = useState<boolean>(false)
  const { tiles } = useTiles()
  const keys = useKeys()
  const { initStockByAddress } = useAvailableFrames()
  const [showValidationErrorDialog, setShowValidationErrorDialog] =
    useState<boolean>(false)
  const [allowUseAsIsButton, setAllowUseAsIsButton] = useState<boolean>(true)
  const [isSmsConsentChecked, setIsSmsConsentChecked] = useState<boolean>(false)
  const [showEmailConsentCheckbox, setShowEmailConsentCheckbox] =
    useState<boolean>(false)
  const [isEmailConsentChecked, setIsEmailConsentChecked] =
    useState<boolean>(false)
  const [address, setAddress] = useState<Address>(
    initStateFromAddress(initialAddress || {})
  )
  const [fullNameKanaInvalid, setFullNameKanaInvalid] = useState<boolean>(false)
  const { t } = useTranslation()
  const PHONE_NUMBER_MSG = t(
    'general.address_form.validation_errors.missing_fields.specific',
    { v1: t('general.address_form.form_fields.phone').toLowerCase() }
  )
  const displayedFormErrorMessage =
    formErrorMessage ||
    (showPhoneError && !address.phoneNumber ? PHONE_NUMBER_MSG : null)

  useOnMount(() => {
    shouldShowEmailConsent()
  })

  const shouldShowEmailConsent = () => {
    if (
      email.value &&
      email.valid &&
      isMarketingConsentCountry(keys.ipCountry)
    ) {
      if (UserManager.hasExplicitEmailConsent(email.value)) {
        return false
      } else {
        // Enabled once migration is complete
        getEmailConsentBraze(toBrazeExternalId(email.value)).then((result) => {
          const consent = result !== 'unsubscribed'
          setShowEmailConsentCheckbox(!consent)
          UserManager.setExplicitEmailConsent(email.value, consent)
        })
      }
    }
    return false
  }

  const emailChanged = (value: string) => {
    setEmail({ value, valid: EmailValidator.validate(value) })
  }

  const handleSmsConsentChange = (isChecked: boolean) => {
    analytics.track(EVENT_NAMES.SMS_CONSENT_CHECKBOX_TAPPED, {
      Source: 'Address Form',
      'Is Checked': isChecked,
    })
    setIsSmsConsentChecked(isChecked)
  }

  const handleEmailConsentChange = (isChecked: boolean) => {
    analytics.track(EVENT_NAMES.EMAIL_CONSENT_CHECKBOX_TAPPED, {
      Source: 'Address Form',
      'Is Checked': isChecked,
    })
    setIsEmailConsentChecked(isChecked)
  }

  const getFormattedAddressFromState = () => {
    const countryId = address.countryId || getDefaultCountryId()
    const formattedAddress: Record<string, string> = { countryId }
    Object.keys(COUNTRIES[countryId].inputs).forEach((fieldName) => {
      formattedAddress[fieldName] = address[fieldName]
        ? address[fieldName].trim()
        : ''
    })
    return formattedAddress
  }

  const handleClose = (newAddressAdded: boolean) => {
    onClose(newAddressAdded)
  }

  const setAddressAndClose = async () => {
    try {
      if (onSubmitShippingAddress) {
        await onSubmitShippingAddress(getFormattedAddressFromState())
      }
    } catch (error: any) {
      if (error?.validationErrors) {
        setShowValidationErrorDialog(true)
        setAllowUseAsIsButton(error.allowEditAddress !== false)
        setValidationError(error.validationErrors.join('. '))
        return
      }
    }
    handleClose(true)
  }

  const onUpdateFunc = () => {
    setSubmitting(true)
    if (onUpdate) {
      onUpdate(getFormattedAddressFromState())
        .then(() => {
          setSubmitting(false)
          setAddressAndClose()
        })
        .catch(() => {
          setSubmitting(false)
        })
    } else {
      setSubmitting(false)
    }
  }

  const onDone = async (ops?: { skipValidation: boolean }) => {
    analytics.track('Address Form Done Button Tapped', {
      address_2_added: !!address.address_2,
    })

    if (ops && ops.skipValidation) {
      const formattedAddress = getFormattedAddressFromState()
      addressStore.setAddressValidationSkipped(true)
      analytics.track('Address Validation Skipped', {
        address: formattedAddress,
      })
      if (isUpdate) {
        onUpdateFunc()
      } else {
        setAddressAndClose()
      }
      return
    }

    addressStore.setAddressValidationSkipped(false)
    setSubmitting(true)
    setEmail((prevEmail) => ({
      ...prevEmail,
      submitted: true,
    }))

    validateInputsAndAddress().then((valid) => {
      setSubmitting(false)
      if (valid) {
        if (isUpdate) {
          onUpdateFunc()
        } else {
          setAddressAndClose()
        }
      } // If input is not valid, warning dialog opens and the user can choose to skip validation.
    })

    if (email.valid) {
      await UserManager.setUserEmail({
        email: email.value,
        source: CAPTURE_EMAIL_SOURCE.checkoutAddress,
        refetchExperiments: false,
        onboardingFlow: null,
        emailConsent: isEmailConsentChecked,
      })
      if (isEmailConsentChecked) {
        // Track the consent event for marketing purposes
        gdprManager.setMarketingConsentGiven(
          CAPTURE_EMAIL_SOURCE.checkoutAddress,
          email.value
        )
        UserManager.setExplicitEmailConsent(email.value, true)
      }
    }

    analytics.capturePhoneNumber(
      address.phoneNumber,
      isSmsConsentChecked,
      'Address Form',
      null
    )

    initStockByAddress({
      address: {
        countryId: address.countryId,
      },
    })
  }

  /* Handle form submit action (pressing Enter on the final input */
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    onDone()
  }

  const showFormErrorMessage = (message: string) => {
    setFormErrorMessage(message)
    containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const capturePhone = () => {
    try {
      return shouldCapturePhone({
        countryId: address.countryId,
        tiles,
        phoneNumber: address?.phoneNumber,
      })
    } catch (e) {
      logger.error('Error checking if phone should be captured', e, {
        countryId: address.countryId ?? 'undefined',
        COUNTRIES: JSON.stringify(COUNTRIES),
      })
      setAddress({ ...address, countryId: COUNTRIES.UNITED_STATES.id })
      return true
    }
  }

  const validateInputsAndAddress = async (): Promise<boolean> => {
    // Validate specific inputs that we can check locally, and the if the local validation passes
    // send the backend address validation request.
    const invalidInputs: Set<string> = new Set()
    let formErrorMessage = null
    const country = COUNTRIES[address.countryId]

    // The kana reading is required when the entered fullName contains kanji — without a
    // reading the backend cannot romanize the name for the UPS label.
    const kanaError =
      language === LANGUAGES.JAPANESE
        ? getNameKanaError(address.fullName, address.fullNameKana)
        : null
    if (kanaError) {
      invalidInputs.add('fullNameKana')
      formErrorMessage = t(NAME_KANA_ERROR_MESSAGE_KEYS[kanaError])
      setFullNameKanaInvalid(true)
    }

    Object.keys(country.inputs).forEach((fieldName) => {
      const fieldValue = address[fieldName]
      if (fieldValue) {
        // Check filled fields
        if (
          fieldName === 'state' &&
          canVerifyCountryStates(country.code) &&
          !verifyState(country.code, fieldValue)
        ) {
          invalidInputs.add(fieldName)
        }
        if (
          fieldName === 'fullName' &&
          !validateFullName(fieldValue, {
            allowJapanese: language === LANGUAGES.JAPANESE,
          })
        ) {
          invalidInputs.add(fieldName)
          formErrorMessage = t(
            'general.address_form.validation_errors.full_name.invalid'
          )
        }

        if (
          country.code === 'IL' &&
          fieldName === 'zipCode' &&
          fieldValue.length < 7
        ) {
          invalidInputs.add(fieldName)
          formErrorMessage =
            'Please use a 7-digit zip code for your address in Israel.'
        }
      }
    })

    if (!validateAddressLatinCharacters(address)) {
      showFormErrorMessage(
        t('general.address_form.validation_errors.non_latin_characters')
      )
      return false
    }

    // This is a hack to not show an error on optional phone number
    // this all code needs to be refactored!!
    if (invalidInputs.has('phoneNumber') && !capturePhone()) {
      invalidInputs.delete('phoneNumber')
    }

    if (invalidInputs.size !== 0) {
      // Indicate missing/invalid input fields - according to our validations (not stripe validation)
      if (!formErrorMessage) {
        const missingFields = Array.from(invalidInputs)
          .map((i) => t(country.inputs[i]?.toLowerCase()))
          .join(', ')
        formErrorMessage = t(
          'general.address_form.validation_errors.missing_fields.specific',
          { v1: missingFields }
        )
      }
      showFormErrorMessage(formErrorMessage)
      return false
    } else if (!isAddressComplete) {
      if (capturePhone() && !address.phoneNumber) {
        showFormErrorMessage(PHONE_NUMBER_MSG)
      } else {
        // Indicate missing/invalid input fields - according to stripe validations (after it passed our validations)
        showFormErrorMessage(
          t('general.address_form.validation_errors.missing_fields.general')
        )
      }
      return false
    }

    if (!email.valid) {
      showFormErrorMessage(
        t('general.address_form.validation_errors.email.invalid')
      )
      return false
    }

    const formattedAddress = getFormattedAddressFromState()

    try {
      // Local validations are done, check with backend if the full address is valid.
      const { isValid, allowEditAddress, validationErrors } =
        await validateAddress(formattedAddress as Address)
      if (!isValid) {
        setShowValidationErrorDialog(true)
        setAllowUseAsIsButton(allowEditAddress)
        setValidationError(validationErrors.join('. '))
      }
      if (!email.valid) {
        showFormErrorMessage(
          t('general.address_form.validation_errors.email.invalid')
        )
      }
      return isValid && email.valid
    } catch (error) {
      const err = (error as any)?.response?.data?.error || error
      logger.warning('Address validation backend error, skipping validation', {
        error: err,
      })
      analytics.track('Address Validation Backend Error', { Error: err })
      // Continue the order flow without indicating an error to the user.
      return true
    }
  }

  const handleAddressChange = (
    updatedAddress: Address,
    isComplete: boolean
  ) => {
    // Stripe's AddressElement doesn't carry fullNameKana — preserve it across changes.
    setAddress((prev: Address) => ({
      ...updatedAddress,
      fullNameKana: prev.fullNameKana,
    }))
    setIsAddressComplete(isComplete)
    setFormErrorMessage('')
  }

  const doneButtonText = isUpdate
    ? t('general.address_form.change_save')
    : t('general.done')
  const doneButton = (
    <TextButton
      variant={VARIANTS.primary}
      onClick={(args?: { skipValidation: boolean }) => onDone(args)}
      loading={submitting}
      testId="add-address-done-button"
    >
      {doneButtonText}
    </TextButton>
  )
  const closeButton = (
    <div data-testid="address-dialog-close-button">
      <IconButton onClick={() => handleClose(false)}>
        <XIcon />
      </IconButton>
    </div>
  )

  const title = isUpdate
    ? t('general.address_form.change_title')
    : t('general.address_form.add_title')

  return (
    <div
      ref={containerRef}
      className={classNames('address-form mixtiles-form', {
        isStripeAddressElementEnabled: true,
      })}
    >
      <S.HeaderContainer>
        <PopupHeader
          title={title}
          leftButton={closeButton}
          rightButton={doneButton}
          sticky
        />
      </S.HeaderContainer>
      <S.FormContainer>
        {displayedFormErrorMessage && (
          <S.ErrorMessageContainer>
            <ErrorMessage message={displayedFormErrorMessage} />
          </S.ErrorMessageContainer>
        )}
        <form onSubmit={handleSubmit}>
          <StripeAddressForm
            mode="shipping"
            address={address}
            onChange={(updatedAddress, isAddressComplete) =>
              handleAddressChange(updatedAddress, isAddressComplete)
            }
            shouldCapturePhone={capturePhone()}
            emailInputParams={{
              value: email.value || '',
              onChange: (newValue) => emailChanged(newValue),
              isInvalid: !!email.submitted && !email.valid,
              onBlur: () => {
                if (email.value) {
                  const trimmedEmail = email.value.trim()
                  if (trimmedEmail !== email.value) {
                    emailChanged(trimmedEmail)
                  }
                }
                shouldShowEmailConsent()
              },
            }}
            smsConsentParams={{
              checked: isSmsConsentChecked,
              onChange: (newValue) => handleSmsConsentChange(newValue),
            }}
            emailConsentParams={
              showEmailConsentCheckbox
                ? {
                    checked: isEmailConsentChecked,
                    onChange: (newValue) => handleEmailConsentChange(newValue),
                  }
                : undefined
            }
            fullNameKanaParams={{
              show: language === LANGUAGES.JAPANESE,
              value: address.fullNameKana || '',
              onChange: (newValue) => {
                setAddress({ ...address, fullNameKana: newValue })
                setFullNameKanaInvalid(false)
                setFormErrorMessage('')
              },
              isInvalid: fullNameKanaInvalid,
            }}
          />
        </form>
      </S.FormContainer>
      <AddressValidationErrorDialog
        open={showValidationErrorDialog}
        address={address}
        validationError={validationError || ''}
        allowUseAsIs={allowUseAsIsButton}
        onUseAsIs={() => {
          setShowValidationErrorDialog(false)
          onDone({ skipValidation: true })
        }}
        onEditAddress={() => setShowValidationErrorDialog(false)}
      />
    </div>
  )
}

export default observer(AddressForm)
